/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { UNSAFE_NavigationContext, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import styles from './GuideNavigationButtons.module.scss';

type GuideNavigationButtonsProps = {
  currentGuideTitle?: string;
};

type GuideNavigationState = {
  guideTitle?: string;
  chatSearch?: { text?: string };
};

type HistoryDirection = 'back' | 'forward';

type StoredHistoryEntry = {
  key: string;
  path: string;
  title?: string;
};

type StoredGuideHistory = {
  furthestKnownHistoryIndex: number;
  entries: Record<number, StoredHistoryEntry>;
};

// MemoryRouter exposes its history index on the navigator in React Router v6.
// Keep iframe history scoped to that router, including when the buttons remount.
const memoryGuideHistories = new WeakMap<object, StoredGuideHistory>();

const HISTORY_LONG_PRESS_MS = 500;
const GUIDE_HISTORY_STORAGE_KEY = 'pearl-guide-navigation-history-v1';

const readStoredGuideHistory = (
  historyIndex: number,
  locationKey: string,
  locationPath: string
): StoredGuideHistory | undefined => {
  try {
    const storedHistory = JSON.parse(sessionStorage.getItem(GUIDE_HISTORY_STORAGE_KEY) || 'null') as StoredGuideHistory;
    const currentEntry = storedHistory?.entries?.[historyIndex];
    if (currentEntry?.key === locationKey && currentEntry.path === locationPath) return storedHistory;
  } catch {
    // Ignore invalid or unavailable storage; navigation still works.
  }
  return undefined;
};

const storeGuideHistory = (history: StoredGuideHistory) => {
  try {
    sessionStorage.setItem(GUIDE_HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Navigation remains functional when session storage is unavailable.
  }
};

export default function GuideNavigationButtons({ currentGuideTitle }: GuideNavigationButtonsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const memoryIndex = (navigator as { index?: number }).index;
  const isMemoryHistory = typeof memoryIndex === 'number';
  const historyIndex = isMemoryHistory
    ? memoryIndex
    : typeof window.history.state?.idx === 'number'
    ? window.history.state.idx
    : 0;
  const locationPath = `${location.pathname}${location.search}${location.hash}`;
  const storedGuideHistory = useRef<StoredGuideHistory>();
  if (storedGuideHistory.current === undefined) {
    const previousHistory = isMemoryHistory
      ? memoryGuideHistories.get(navigator)
      : readStoredGuideHistory(historyIndex, location.key, locationPath);
    storedGuideHistory.current = previousHistory || {
      furthestKnownHistoryIndex: historyIndex,
      entries: {},
    };
  }
  const guideHistory = storedGuideHistory.current;
  if (isMemoryHistory) memoryGuideHistories.set(navigator, guideHistory);
  const longPressTimer = useRef<number>();
  const suppressNavigationClick = useRef(false);
  const [historyMenu, setHistoryMenu] = useState<{
    anchorEl: HTMLElement;
    direction: HistoryDirection;
  }>();

  if (navigationType === 'PUSH' && guideHistory.entries[historyIndex]?.key !== location.key) {
    guideHistory.furthestKnownHistoryIndex = historyIndex;
    Object.keys(guideHistory.entries).forEach((index) => {
      if (Number(index) > historyIndex) delete guideHistory.entries[Number(index)];
    });
  } else {
    guideHistory.furthestKnownHistoryIndex = Math.max(guideHistory.furthestKnownHistoryIndex, historyIndex);
  }

  const navigationState = location.state as GuideNavigationState | null;
  const activeGuideTitle = navigationState?.guideTitle || navigationState?.chatSearch?.text || currentGuideTitle;
  guideHistory.entries[historyIndex] = {
    key: location.key,
    path: locationPath,
    title: activeGuideTitle,
  };

  const canNavigateBack = historyIndex > 0;
  const canNavigateForward = historyIndex < guideHistory.furthestKnownHistoryIndex;
  const previousGuideTitle = guideHistory.entries[historyIndex - 1]?.title;
  const nextGuideTitle = guideHistory.entries[historyIndex + 1]?.title;
  const guideTitlesByHistoryIndex = Object.entries(guideHistory.entries)
    .filter((entry): entry is [string, StoredHistoryEntry & { title: string }] => !!entry[1].title)
    .map(([index, entry]) => [Number(index), entry.title] as const);
  const backHistory = guideTitlesByHistoryIndex
    .filter(([index]) => index < historyIndex)
    .sort(([leftIndex], [rightIndex]) => rightIndex - leftIndex);
  const forwardHistory = guideTitlesByHistoryIndex
    .filter(([index]) => index > historyIndex)
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex);

  const clearLongPressTimer = () => {
    if (longPressTimer.current != null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = undefined;
  };

  const openHistoryMenu = (anchorEl: HTMLElement, direction: HistoryDirection) => {
    const entries = direction === 'back' ? backHistory : forwardHistory;
    if (entries.length) setHistoryMenu({ anchorEl, direction });
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>, direction: HistoryDirection) => {
    clearLongPressTimer();
    // Suppression belongs to the previous long press, never a new click.
    suppressNavigationClick.current = false;
    const entries = direction === 'back' ? backHistory : forwardHistory;
    if (!entries.length) return;
    const anchorEl = event.currentTarget;
    longPressTimer.current = window.setTimeout(() => {
      suppressNavigationClick.current = true;
      openHistoryMenu(anchorEl, direction);
    }, HISTORY_LONG_PRESS_MS);
  };

  const handleContextMenu = (event: ReactMouseEvent<HTMLElement>, direction: HistoryDirection) => {
    event.preventDefault();
    const entries = direction === 'back' ? backHistory : forwardHistory;
    if (!entries.length) return;
    suppressNavigationClick.current = true;
    openHistoryMenu(event.currentTarget, direction);
  };

  const handleNavigationClick = (offset: number) => {
    if (suppressNavigationClick.current) {
      suppressNavigationClick.current = false;
      return;
    }
    navigate(offset);
  };

  useEffect(() => {
    if (!isMemoryHistory) storeGuideHistory(guideHistory);
  }, [guideHistory, historyIndex, isMemoryHistory, location.key, locationPath]);

  useEffect(() => {
    if (!isMemoryHistory) return;

    const handleHistoryMouseButton = (event: MouseEvent) => {
      if (event.button !== 3 && event.button !== 4) return;
      // Embedded browsers may navigate on different mouse events. Cancel each
      // default action, but navigate only once, on release.
      event.preventDefault();
      if (event.type !== 'mouseup') return;
      if (event.button === 3 && canNavigateBack) navigate(-1);
      if (event.button === 4 && canNavigateForward) navigate(1);
    };

    const events = ['mousedown', 'mouseup', 'auxclick'] as const;
    events.forEach((type) => window.addEventListener(type, handleHistoryMouseButton, true));
    return () => {
      events.forEach((type) => window.removeEventListener(type, handleHistoryMouseButton, true));
    };
  }, [isMemoryHistory, canNavigateBack, canNavigateForward, navigate]);

  useEffect(() => {
    return () => {
      if (longPressTimer.current != null) window.clearTimeout(longPressTimer.current);
    };
  }, []);

  return (
    <div className={styles.navigationButtons}>
      <Tooltip
        title={canNavigateBack ? (previousGuideTitle ? `Back to ${previousGuideTitle}` : 'Go back') : ''}
        disableInteractive
      >
        <span>
          <Button
            type="button"
            aria-label="Back"
            className={styles.navigationButton}
            disabled={!canNavigateBack}
            onPointerDown={(event) => handlePointerDown(event, 'back')}
            onPointerUp={clearLongPressTimer}
            onPointerCancel={clearLongPressTimer}
            onPointerLeave={clearLongPressTimer}
            onContextMenu={(event) => handleContextMenu(event, 'back')}
            onClick={() => handleNavigationClick(-1)}
          >
            <ArrowBackIcon />
          </Button>
        </span>
      </Tooltip>
      {canNavigateForward && (
        <Tooltip title={nextGuideTitle ? `Forward to ${nextGuideTitle}` : 'Go forward'} disableInteractive>
          <span>
            <Button
              type="button"
              aria-label="Forward"
              className={styles.navigationButton}
              onPointerDown={(event) => handlePointerDown(event, 'forward')}
              onPointerUp={clearLongPressTimer}
              onPointerCancel={clearLongPressTimer}
              onPointerLeave={clearLongPressTimer}
              onContextMenu={(event) => handleContextMenu(event, 'forward')}
              onClick={() => handleNavigationClick(1)}
            >
              <ArrowForwardIcon />
            </Button>
          </span>
        </Tooltip>
      )}
      <Menu
        anchorEl={historyMenu?.anchorEl}
        open={!!historyMenu}
        onClose={() => {
          clearLongPressTimer();
          suppressNavigationClick.current = false;
          setHistoryMenu(undefined);
        }}
        MenuListProps={{ 'aria-label': `${historyMenu?.direction || 'back'} history` }}
      >
        {(historyMenu?.direction === 'forward' ? forwardHistory : backHistory).map(([index, title]) => (
          <MenuItem
            key={index}
            onClick={() => {
              setHistoryMenu(undefined);
              suppressNavigationClick.current = false;
              navigate(index - historyIndex);
            }}
          >
            {title}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
